import { useState, useEffect, useRef, RefObject } from 'react';

interface UseStickyHeaderOptions {
	/**
	 * Threshold in pixels before the header becomes sticky
	 * @default 0
	 */
	threshold?: number;
	/**
	 * Reference to the scroll container. If not provided,
	 * will look for the nearest .route-cache-item parent
	 */
	scrollContainerRef?: RefObject<HTMLElement>;
}

interface UseStickyHeaderResult {
	/**
	 * Whether the header is currently in sticky mode
	 */
	isSticky: boolean;
	/**
	 * Current scroll position
	 */
	scrollY: number;
	/**
	 * Ref to attach to the header element for measurements
	 */
	headerRef: RefObject<HTMLDivElement>;
	/**
	 * Ref to attach to a sentinel element (placeholder)
	 */
	sentinelRef: RefObject<HTMLDivElement>;
}

/**
 * Hook to manage sticky header behavior with scroll detection
 * Designed to work with the route-cache-item scroll container
 */
export const useStickyHeader = (
	options: UseStickyHeaderOptions = {}
): UseStickyHeaderResult => {
	const { threshold = 0 } = options;
	const [isSticky, setIsSticky] = useState(false);
	const [scrollY, setScrollY] = useState(0);
	const headerRef = useRef<HTMLDivElement>(null);
	const sentinelRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const headerElement = headerRef.current;
		if (!headerElement) return;

		// Find the scroll container (route-cache-item)
		const scrollContainer = options.scrollContainerRef?.current
			|| headerElement.closest('.route-cache-item') as HTMLElement;

		if (!scrollContainer) {
			console.warn('useStickyHeader: Could not find scroll container');
			return;
		}

		const handleScroll = () => {
			const currentScrollY = scrollContainer.scrollTop;
			setScrollY(currentScrollY);

			// Use the sentinel's position to determine stickiness
			// The sentinel is placed where the header originally was
			if (sentinelRef.current) {
				const sentinelRect = sentinelRef.current.getBoundingClientRect();
				const containerRect = scrollContainer.getBoundingClientRect();
				// If the sentinel is above or at the container top, header should be sticky
				const shouldBeSticky = sentinelRect.top <= containerRect.top + threshold;
				setIsSticky(shouldBeSticky);
			} else {
				// Fallback to simple scroll threshold
				setIsSticky(currentScrollY > threshold);
			}
		};

		// Initial check
		handleScroll();

		scrollContainer.addEventListener('scroll', handleScroll, { passive: true });

		return () => {
			scrollContainer.removeEventListener('scroll', handleScroll);
		};
	}, [threshold, options.scrollContainerRef]);

	return {
		isSticky,
		scrollY,
		headerRef,
		sentinelRef
	};
};
